/**
 * @fileoverview Case Ingestion API Service - RTK Query endpoints for ingestion audit records.
 *
 * Endpoints:
 * - GET /api/matters/:id/case-ingestion/ - Fetch audit record
 * - POST /api/matters/:id/case-ingestion/ - Create audit record
 * - PATCH /api/matters/:id/case-ingestion/ - Update section status
 * - POST /api/matters/:id/case-ingestion/complete/ - Mark ingestion complete
 */
import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  CaseIngestionResult,
  CompleteCaseIngestionRequest,
  UpdateCaseIngestionSectionsRequest,
} from '../types/case-ingestion-types';
import { baseQueryWithErrorHandling } from './baseQuery';

/**
 * RTK Query API client for case ingestion audit records.
 */
export const caseIngestionApiClient = createApi({
  reducerPath: 'caseIngestionApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['CaseIngestion'] as const,
  endpoints: (builder) => ({
    /** Get the ingestion audit record for a matter (for resume/init) */
    getCaseIngestion: builder.query<CaseIngestionResult, string>({
      query: (matterId) => `api/matters/${matterId}/case-ingestion/`,
      providesTags: (result, error, matterId) => [
        { type: 'CaseIngestion', id: matterId },
      ],
    }),

    /** Create an ingestion audit record when matter enters CASE_INGESTION status */
    createCaseIngestion: builder.mutation<
      CaseIngestionResult,
      { matterId: string }
    >({
      query: ({ matterId }) => ({
        url: `api/matters/${matterId}/case-ingestion/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'CaseIngestion', id: matterId },
      ],
    }),

    /** Update one or more section statuses in the audit record (batch) */
    updateCaseIngestionSections: builder.mutation<
      CaseIngestionResult,
      { matterId: string; data: UpdateCaseIngestionSectionsRequest }
    >({
      query: ({ matterId, data }) => ({
        url: `api/matters/${matterId}/case-ingestion/`,
        method: 'PATCH',
        body: data,
      }),
      // Manual cache update from mutation response (avoids redundant GET refetch)
      async onQueryStarted({ matterId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedAuditRecord } = await queryFulfilled;
          dispatch(
            caseIngestionApiClient.util.updateQueryData(
              'getCaseIngestion',
              matterId,
              () => updatedAuditRecord,
            ),
          );
        } catch {
          // On error, let query refetch naturally on next access
        }
      },
    }),

    /** Complete the ingestion — sets completed_by and completed_at */
    completeCaseIngestion: builder.mutation<
      CaseIngestionResult,
      { matterId: string; data?: CompleteCaseIngestionRequest }
    >({
      query: ({ matterId, data }) => ({
        url: `api/matters/${matterId}/case-ingestion/complete/`,
        method: 'POST',
        body: data ?? {},
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'CaseIngestion', id: matterId },
      ],
    }),
  }),
});

export const {
  useGetCaseIngestionQuery,
  useCreateCaseIngestionMutation,
  useUpdateCaseIngestionSectionsMutation,
  useCompleteCaseIngestionMutation,
} = caseIngestionApiClient;
